// The single place this app talks to the API.
//
// Two rules that are easy to break and expensive to break:
//
// 1. NEVER send `X-App-Version`. middleware/appVersion.js lets a request with no
//    header through, which is the only reason a browser works at all. If this
//    client ever sends it — copied from the Android HTTP config, most likely —
//    then raising MIN_APP_VERSION during an Android incident takes the console
//    down with it. Two clients, one kill switch, and only one of them is meant
//    to be behind it. (WEB-APP-PLAN §10)
//
// 2. The token lives in memory, mirrored to sessionStorage so a refresh does not
//    sign the user out. NEVER localStorage: a web token is 12 hours and the
//    laptop may not be theirs alone. (§4.3)

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'bt.web.token';
const USER_KEY = 'bt.web.user';

export type SessionUser = {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'owner' | 'manager';
  is_super_admin?: boolean;
  web_app_access?: boolean;
  avatar?: string;
};

let token: string | null = null;
let user: SessionUser | null = null;

// sessionStorage throws in some privacy modes. A console that will not render
// because storage is blocked is worse than one that forgets on refresh.
function safeRead(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeWrite(key: string, value: string | null) {
  try { value === null ? sessionStorage.removeItem(key) : sessionStorage.setItem(key, value); } catch { /* ignore */ }
}

token = safeRead(TOKEN_KEY);
try { user = JSON.parse(safeRead(USER_KEY) ?? 'null'); } catch { user = null; }

export function getToken() { return token; }
export function getUser() { return user; }

export function setSession(nextToken: string, nextUser: SessionUser) {
  token = nextToken;
  user = nextUser;
  safeWrite(TOKEN_KEY, nextToken);
  safeWrite(USER_KEY, JSON.stringify(nextUser));
}

export function clearSession() {
  token = null;
  user = null;
  safeWrite(TOKEN_KEY, null);
  safeWrite(USER_KEY, null);
}

/** What went wrong, in a shape the UI can branch on rather than parse. */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
  /** The organisation has not paid. Show the billing screen, not "access denied". */
  get isSubscriptionInactive() { return this.status === 402; }
  /** The session is gone — expired, revoked, or the account was deleted. */
  get isUnauthenticated() { return this.status === 401; }
  /** Signed in, but this owner has not been granted the console. */
  get isWebAccessDenied() { return this.status === 403 && this.code === 'web_access_not_granted'; }
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal };

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Deliberately no X-App-Version. See the note at the top of this file.

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch {
    // Render sleeps on the free tier and a cold start has been measured at over
    // 90 seconds. An owner seeing six tiles fail at once deserves the real
    // reason rather than "Failed to fetch".
    throw new ApiError(0, 'Could not reach BhoomiTrack. Check your connection and try again.');
  }

  const text = await res.text();
  let payload: any = null;
  try { payload = text ? JSON.parse(text) : null; } catch { /* non-JSON error page */ }

  if (!res.ok) {
    const message = payload?.message || `Request failed (${res.status})`;
    const err = new ApiError(res.status, message, payload?.code);
    if (err.isUnauthenticated) clearSession();
    throw err;
  }
  return payload as T;
}
